import React from 'react';
import { Link } from 'react-router-dom';
import {
  FaDesktop, FaServer, FaCogs, FaDatabase, FaKey, FaCloud,
  FaHeadset, FaShieldAlt, FaSyncAlt, FaArrowRight, FaCheckCircle
} from 'react-icons/fa';
import { motion } from 'motion/react';
import ScrollReveal from './ScrollReveal';
import PageNavbar from './PageNavbar';
import {
  defaultViewport,
  staggerContainer,
  staggerItem,
  getRevealProps,
} from '../motion/scrollMotion';
import Footer from './Footer';
import './ManagedITPage.css';

const services = [
  {
    icon: <FaDesktop />,
    title: 'Endpoint Management',
    href: '/services/endpoint-management',
    desc: 'Centralized endpoint management with EDR, policy enforcement, automated compliance, and remote lock/wipe across workstations, servers, and mobile devices.',
  },
  {
    icon: <FaServer />,
    title: 'Server & Network Monitoring',
    href: '/services/server-network-monitoring',
    desc: 'Proactive monitoring of servers, switches, firewalls, and network infrastructure with real-time alerting and performance analytics.',
  },
  {
    icon: <FaCogs />,
    title: 'Patch Management',
    href: '/services/patch-management',
    desc: 'Automated and validated patching across operating systems and critical applications — tested, staged rollouts, and zero-day fast-tracking.',
  },
  {
    icon: <FaDatabase />,
    title: 'Backup & Disaster Recovery',
    href: '/services/backup-disaster-recovery',
    desc: 'Reliable on-site and offsite backups with encryption, regular integrity testing, DR failover simulations, and ransomware-resilient architecture.',
  },
  {
    icon: <FaKey />,
    title: 'Identity & Access Management',
    href: '/services/identity-access-management',
    desc: 'Active Directory, Azure AD/Entra ID, SSO, multi-factor authentication, privileged access management, and access governance reviews.',
  },
  {
    icon: <FaCloud />,
    title: 'Cloud Infrastructure Support',
    href: '/services/cloud-infrastructure-support',
    desc: 'Management and optimization across AWS, Azure, and GCP — migration planning, cost optimization, IaC provisioning, and multi-cloud strategy.',
  },
  {
    icon: <FaHeadset />,
    title: 'Remote Helpdesk Support',
    href: '/services/remote-helpdesk-support',
    desc: 'SLA-driven L1/L2/L3 remote IT support for end users — fast resolution, professional service, and seamless escalation for complex issues.',
  },
];

const differentiators = [
  {
    icon: <FaShieldAlt />,
    title: 'Security-First Operations',
    desc: 'Every endpoint, patch, and configuration is managed with cybersecurity principles baked in — not bolted on after the fact.',
  },
  {
    icon: <FaSyncAlt />,
    title: 'Automated & Validated',
    desc: 'Automation handles the repeatable work — patching, backup verification, compliance reporting — while our engineers handle exceptions.',
  },
  {
    icon: <FaCheckCircle />,
    title: 'SLA-Driven Delivery',
    desc: 'Response and resolution times you can plan around. Transparent reporting on every ticket, every patch cycle, every backup test.',
  },
];

const ManagedITPage = () => {
  return (
    <div className="mit-page">
      <PageNavbar />

      {/* Hero */}
      <section className="mit-hero">
        <div className="mit-hero-background"></div>
        <div className="mit-hero-stars"></div>
        <ScrollReveal className="mit-hero-content" animateOnMount variant="fadeUp" duration={1.05}>
          <span className="mit-badge">Managed IT Services</span>
          <h1 className="mit-hero-title">
            Reliable, Secure, and Scalable<br />
            <span className="mit-accent">IT Operations.</span>
          </h1>
          <p className="mit-hero-description">
            Managed IT services designed with a security-first mindset. From endpoint management
            to cloud infrastructure, we keep your technology running securely and efficiently —
            with cybersecurity principles embedded into every process.
          </p>
        </ScrollReveal>
      </section>

      {/* Not Generic IT Support — differentiators */}
      <section className="mit-diff-section">
        <div className="mit-container">
          <motion.div className="mit-section-header light" {...getRevealProps('fadeUp')}>
            <span className="mit-block-tag">Not Generic IT Support</span>
            <h2 className="mit-section-title light">Secure IT Operations, Not Helpdesk Tickets.</h2>
            <p className="mit-section-subtitle light">
              Most managed IT providers focus on keeping systems running. Proven InfoSec goes further —
              we manage your IT infrastructure with cybersecurity principles embedded into every
              process, from endpoint hardening to cloud configuration management.
            </p>
          </motion.div>

          <motion.div
            className="mit-diff-grid"
            initial="hidden"
            whileInView="visible"
            viewport={defaultViewport}
            variants={staggerContainer}
          >
            {differentiators.map((d, i) => (
              <motion.div className="mit-diff-card" key={i} variants={staggerItem}>
                <div className="mit-diff-icon">{d.icon}</div>
                <h3 className="mit-diff-title">{d.title}</h3>
                <p className="mit-diff-desc">{d.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services We Manage */}
      <section className="mit-services-section">
        <div className="mit-container">
          <motion.div className="mit-section-header" {...getRevealProps('fadeUp')}>
            <span className="mit-block-tag">Services We Manage</span>
            <h2 className="mit-section-title">Seven Pillars of Managed IT</h2>
            <p className="mit-section-subtitle">
              Comprehensive IT operations coverage — from endpoint and patch management to cloud
              infrastructure, identity, and 24/7 monitoring.
            </p>
          </motion.div>

          <motion.div
            className="mit-services-grid"
            initial="hidden"
            whileInView="visible"
            viewport={defaultViewport}
            variants={{
              ...staggerContainer,
              visible: { transition: { staggerChildren: 0.08, delayChildren: 0.06 } },
            }}
          >
            {services.map((s, i) => (
              <motion.div className="mit-service-card" key={i} variants={staggerItem}>
                <div className="mit-service-icon">{s.icon}</div>
                <h3 className="mit-service-title">{s.title}</h3>
                <p className="mit-service-desc">{s.desc}</p>
                <Link to={s.href} className="mit-service-link">
                  Learn more <FaArrowRight />
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Highlight: Endpoint Management deep dive (left text, right capability list) */}
      <section className="mit-highlight-section">
        <div className="mit-container">
          <div className="mit-highlight-grid">
            <motion.div className="mit-highlight-text" {...getRevealProps('slideLeft')}>
              <span className="mit-block-tag">Endpoint Management</span>
              <h2 className="mit-section-title light" style={{ textAlign: 'left' }}>
                Centralized Control Across Every Device.
              </h2>
              <p className="mit-block-body">
                Modern organizations operate with distributed workforces across multiple locations
                and device types. Endpoint management provides centralized control over device
                configuration, security policies, software deployment, and compliance monitoring —
                from workstations and servers to mobile devices.
              </p>
              <ul className="mit-bullet-list">
                <li>Centralized device inventory and asset management</li>
                <li>Automated software deployment and updates</li>
                <li>EDR deployment and continuous monitoring</li>
                <li>Security policy enforcement and compliance reporting</li>
                <li>Remote lock, wipe, and device recovery</li>
                <li>Integration with identity and access management</li>
              </ul>
            </motion.div>

            <motion.div className="mit-capability-card" {...getRevealProps('slideRight', 0.12)}>
              <span className="mit-block-tag">Backup & Disaster Recovery</span>
              <h3 className="mit-capability-title">Ransomware-Resilient Recovery.</h3>
              <p className="mit-capability-body">
                Reliable backup solutions and tested disaster recovery plans designed to minimize
                downtime and data loss in any failure scenario — including ransomware attack
                recovery and regional outages.
              </p>
              <div className="mit-capability-list">
                <div className="mit-capability-item">
                  <FaCheckCircle className="mit-check-icon" />
                  <span>Automated backup of servers, workstations, and cloud data</span>
                </div>
                <div className="mit-capability-item">
                  <FaCheckCircle className="mit-check-icon" />
                  <span>On-site and offsite backup with encryption</span>
                </div>
                <div className="mit-capability-item">
                  <FaCheckCircle className="mit-check-icon" />
                  <span>Regular backup integrity testing and validation</span>
                </div>
                <div className="mit-capability-item">
                  <FaCheckCircle className="mit-check-icon" />
                  <span>DR testing and failover simulations</span>
                </div>
                <div className="mit-capability-item">
                  <FaCheckCircle className="mit-check-icon" />
                  <span>Ransomware-resilient backup architecture</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mit-cta-section">
        <div className="mit-container">
          <motion.div className="mit-cta-card" {...getRevealProps('scaleUp')}>
            <div className="mit-cta-left">
              <h2 className="mit-cta-title">Outsource IT, not your security posture.</h2>
              <p className="mit-cta-desc">
                Talk to our team about a fully managed or co-managed IT engagement — designed for
                organizations that demand security as a core part of every operation.
              </p>
              <div className="mit-cta-actions">
                <button className="mit-cta-btn primary">Book Consultation <FaArrowRight /></button>
                <button className="mit-cta-btn secondary">View All Services</button>
              </div>
            </div>
            <div className="mit-cta-right">
              <div className="mit-cta-stat">
                <div className="mit-cta-stat-value">Resilient</div>
                <div className="mit-cta-stat-label">Uptime &amp; DR</div>
              </div>
              <div className="mit-cta-stat">
                <div className="mit-cta-stat-value">24/7</div>
                <div className="mit-cta-stat-label">Monitoring & Response</div>
              </div>
              <div className="mit-cta-stat">
                <div className="mit-cta-stat-value">SLA</div>
                <div className="mit-cta-stat-label">Driven Service Delivery</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ManagedITPage;
